// import { useLanguage } from "@/api/AppApi/config/use-language";
import { apiConfig } from "../../config";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { cartItemSavedCountAtom } from "@/hooks/cartAtoms";
import { useAtom, useSetAtom } from "jotai";
import ApiCart, { APIGetUserLocation } from "./api-cart";
import {
  IGetCart,
  IPostPaymentMethodAvailableResult,
  ISubmitCheckoutResponse,
  ICartManageItemParams,
  ISetPaymentMethodParams,
} from "./interfaces/cart-interfaces";
import { useGetCompanyQuery } from "../company";
import { getKey } from "../../common-interfaces/quiries-keys/queries-keys";
import { Keys } from "../../common-interfaces/quiries-keys/keysValues";
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { usePathname, useSearchParams } from "next/navigation";
import useSellingMode from "@/hooks/useSellingMode";
import { reportUserFacingFailure } from "@/utils/logging/clientErrorLogger";
import {
  getFbcCookie,
  getFbpCookie,
  getTtpCookie,
  getTtclidCookie,
  getTwclidCookie,
  getFbLoginIdCookie,
} from "@/utils/functions/cookieOperations";

export const useCartApi = () => new ApiCart(apiConfig());

export const useGetCartQuery = <TData = IGetCart>(
  options?: UseQueryOptions<IGetCart, unknown, TData>
) => {
  const getCompany = useGetCompanyQuery();
  const { show_cart } = getCompany?.data?.result?.theme?.settings ?? {};

  const { enableSells, isFetching } = useSellingMode();

  const cartApi = useCartApi();
  return useQuery<IGetCart, unknown, TData>([...getKey(Keys.get_cart)], cartApi.getCart, {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !!(show_cart && !!enableSells && !isFetching),
    ...(options as any),
  });
};

import { updateGetCartQueryData } from "./cart-helpers";
export { updateGetCartQueryData };
export const useGetShippingMethods = (areaId: string) => {
  const cartApi = useCartApi();
  return useQuery(
    [...(getKey(Keys.get_shipping_mthods) + areaId)],
    () => cartApi.getShippingMethods(areaId),
    {
      refetchOnWindowFocus: false,
      enabled: +areaId > 0,
    },
  );
};
export const useGetPickupMethods = () => {
  const cartApi = useCartApi();
  return useQuery([Keys.get_pickup_mthods], () => cartApi.getpickupMethods(), {
    refetchOnWindowFocus: false,
  });
};

export const useSetShippingMethod = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const fbcFromCookie = getFbcCookie();
  const fbpCookie = getFbpCookie();
  const ttpCookie = getTtpCookie();
  const ttclidCookie = getTtclidCookie();
  const twclidCookie = getTwclidCookie();
  const fb_login_idCookie = getFbLoginIdCookie();
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(
    (vars: {
      price_zone_id: number;
      shipping_method_id: number;
      event_id?: string;
    }) => {
      return cart.setShippingMethod({
        ...vars,
        fbc: fbcFromCookie ?? undefined,
        fbp: fbpCookie ?? undefined,
        ttp: ttpCookie ?? undefined,
        ttclid: ttclidCookie ?? undefined,
        twclid: twclidCookie ?? undefined,
        fb_login_id: fb_login_idCookie ?? undefined,
      });
    },
    {
      onSuccess: (res) => {
        onSuccess(res);
        updateGetCartQueryData(queryClient, res as IGetCart);
      },
      onError: onError,
    },
  );
};
export const useSetPaymentMethod = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const fbcFromCookie = getFbcCookie();
  const fbpCookie = getFbpCookie();
  const ttpCookie = getTtpCookie();
  const ttclidCookie = getTtclidCookie();
  const twclidCookie = getTwclidCookie();
  const fb_login_idCookie = getFbLoginIdCookie();

  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(
    (vars: ISetPaymentMethodParams) => {
      const augmented: ISetPaymentMethodParams = {
        ...vars,
        fbc: fbcFromCookie ?? null,
        fbp: fbpCookie ?? null,
        ttp: ttpCookie ?? null,
        ttclid: ttclidCookie ?? null,
        twclid: twclidCookie ?? null,
        fb_login_id: fb_login_idCookie ?? null,
      };
      return cart.setPaymentMethod(augmented);
    },
    {
      onSuccess: (res) => {
        onSuccess(res);
        updateGetCartQueryData(queryClient, res as IGetCart);
      },
      onError: onError,
    },
  );
};
export const useManageCartItem = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any, variables: ICartManageItemParams) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  const countCartItemSaved = useSetAtom(cartItemSavedCountAtom);
  const couponAPI = usePostCoupon({
    onSuccess: () => {
      localStorage.removeItem("coupon_referer");
    },
    onError: () => {},
  });

  const fbcFromCookie = getFbcCookie();
  const fbpCookie = getFbpCookie();
  const ttpCookie = getTtpCookie();
  const ttclidCookie = getTtclidCookie();
  const twclidCookie = getTwclidCookie();
  const fb_login_idCookie = getFbLoginIdCookie();

  return useMutation(
    (vars: ICartManageItemParams) => {
      const augmented: ICartManageItemParams = {
        ...vars,
        fbc: fbcFromCookie ?? null,
        fbp: fbpCookie ?? null,
        ttp: ttpCookie ?? null,
        ttclid: ttclidCookie ?? null,
        twclid: twclidCookie ?? null,
        fb_login_id: fb_login_idCookie ?? null,
      };
      return cart.manageCartItem(augmented);
    },
    {
      onSuccess: (res, variables) => {
        updateGetCartQueryData(queryClient, res as IGetCart);
        // Ordered deliberately: the cache is current before anyone is told the
        // write landed, so a watcher can read the new basket straight away.
        countCartItemSaved((count) => count + 1);

        // React Query runs onSuccess inside the same try block as the mutation,
        // so a throw here lands in onError and shows an error popup for a cart
        // write that succeeded. This is analytics, coupons and navigation —
        // none of it should be able to do that. Log it instead.
        try {
          // Throws outright when site data is blocked (in-app browsers).
          let coupon: string | null = null;
          try {
            coupon = localStorage.getItem("coupon_referer");
          } catch {
            coupon = null;
          }
          if (coupon) {
            couponAPI.mutate({ code: coupon, country_code: "" });
          }
          onSuccess(res, variables);
        } catch (err: any) {
          reportUserFacingFailure({
            error: err,
            context: "manage_cart_item_on_success",
            extra: {
              cart_item_id: variables?.cartId ?? null,
              product_id: (variables as any)?.id ?? null,
              quantity: (variables as any)?.quantity ?? null,
              items_in_response: (res as IGetCart)?.result?.items?.length ?? -1,
            },
          });
        }
      },
      onError: onError,
      mutationKey: ["post-item"],
    },
  );
};

export const useRemoveCartItem = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const cart_item_referer = searchParams.get("cart_item_referer");
  return useMutation(cart.removeCartItem, {
    onSuccess: (res) => {
      updateGetCartQueryData(queryClient, res);
      onSuccess(res);
      if (!!cart_item_referer) router.push(pathName);
    },
    onError: onError,
  });
};

export const useClearCart = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();

  return useMutation(cart.clearCart, {
    onSuccess: (res) => {
      updateGetCartQueryData(queryClient, res as IGetCart);
      onSuccess(res);
    },
    onError: onError,
  });
};

export const usePostCoupon = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(cart.postCoupons, {
    onSuccess: (res) => {
      onSuccess(res);
      updateGetCartQueryData(queryClient, res as IGetCart);
    },
    onError: onError,
  });
};

export const useDeleteCoupon = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(cart.deleteCoupons, {
    onSuccess: (res) => {
      onSuccess(res);
      updateGetCartQueryData(queryClient, res as IGetCart);
    },
    onError: onError,
  });
};

export const usePostRefreshCart = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(cart.refresh, {
    onSuccess: (res) => {
      updateGetCartQueryData(queryClient, res as IGetCart);
      onSuccess(res);
    },
    onError: onError,
  });
};

export const useSubmitCheckout = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: ISubmitCheckoutResponse) => void;
  onError: (err: any) => void;
}) => {
  const getCart = useGetCartQuery();
  const cart = useCartApi();
  const queryClient = useQueryClient();
  const [isSignedIn] = useAtom(isSignedInAtom);

  const fbcFromCookie = getFbcCookie();
  const fbpCookie = getFbpCookie();
  const ttpCookie = getTtpCookie();
  const ttclidCookie = getTtclidCookie();
  const twclidCookie = getTwclidCookie();
  const fbLoginIdCookie = getFbLoginIdCookie();

  return useMutation(
    (body: any) => {
      const augmented = {
        ...body,
        fbc: fbcFromCookie ?? null,
        fbp: fbpCookie ?? null,
        ttp: ttpCookie ?? null,
        ttclid: ttclidCookie ?? null,
        twclid: twclidCookie ?? null,
        fb_login_id: fbLoginIdCookie ?? null,
      };
      return cart.submitCheckout(augmented);
    },
    {
      onSuccess: (res) => {
        onSuccess(res);
        getCart.refetch();
        if (isSignedIn) {
          queryClient.invalidateQueries(getKey(Keys.get_all_orders));
          queryClient.invalidateQueries(getKey(Keys.get_returned_invoices));
          queryClient.invalidateQueries(getKey(Keys.get_exchange_invoices));
          queryClient.invalidateQueries(getKey(Keys.get_order_details));
        }
      },
      onError: onError,
    },
  );
};

export const useGetPaymentQuery = ({
  track_id,
  enabled,
}: {
  track_id: string;
  enabled: boolean;
}) => {
  const cartAPI = useCartApi();
  return useQuery(
    [...getKey(Keys.get_payment), track_id],
    () => cartAPI.getPayments(track_id),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
    },
  );
};

export const useGetPaymentMethods = ({
  country_code,
  delivery_type,
  track_id,
  total,
  exchange_grand_total,
  enabled = true,
}: {
  country_code: string;
  delivery_type?: string;
  track_id?: number | string;
  total?: number;
  exchange_grand_total?: number;
  enabled?: boolean;
}) => {
  const cartApi = useCartApi();
  return useQuery(
    [...(getKey(Keys.get_payment_mthods) + country_code + (delivery_type || "")), total ?? 0, exchange_grand_total ?? 0,track_id],
    () => cartApi.getPaymentMethods(country_code, exchange_grand_total, delivery_type, track_id),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
    },
  );
};

export const useGetUserLocation = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  return useQuery([...getKey(Keys.user_locaion)], () => APIGetUserLocation(), {
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

export const useGetDeliveryBranches = ({
  address_id,
  area_id,
  enabled,
}: {
  address_id?: number;
  area_id?: number;
  enabled?: boolean;
}) => {
  const cartApi = useCartApi();
  return useQuery(
    [
      ...(getKey(Keys.get_Delivery_branches) +
        (address_id ?? area_id)?.toString()!),
    ],
    () => cartApi.getDeliveryBranches(address_id!, area_id!),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
    },
  );
};

export const useSubmitPaymentMethodAvailable = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IPostPaymentMethodAvailableResult) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  return useMutation(cart.postPaymentMethodAvailable, {
    onSuccess: (res) => {
      onSuccess(res);
    },
    onError: onError,
  });
};
